import React from "react";
import { Link, useParams } from "react-router-dom";
import legionsTimeline from "../data/LegionsTimeline.json";
import militaryDossiers from "../data/militaryDossiers.json";

const PERIOD_LABELS = {
  invasion: "Claudian Invasion and Early Occupation",
  boudica: "Boudican Revolt and Recovery",
  flavian: "Flavian Consolidation and the Welsh Campaigns",
  hadrian: "Hadrianic Frontier System",
  antonine: "Antonine Advance",
  late2nd: "Late Second-Century Strain",
  severan: "Severan Crisis and Northern War",
  late: "Late Roman Transformation",
};

const LEGION_LABELS = {
  II: "Legio II Augusta",
  XX: "Legio XX Valeria Victrix",
  VI: "Legio VI Victrix",
};

function Field({ title, children }) {
  return (
    <section className="rounded-2xl border border-stone-800 bg-[#171411] p-6">
      <h2 className="mb-3 text-2xl font-bold text-amber-200">{title}</h2>
      <p className="text-base leading-8 text-stone-300">{children}</p>
    </section>
  );
}

export default function MilitaryDossier() {
  const { id } = useParams();

  const timelineItem = legionsTimeline.find((item) => item.id === id);
  const dossier = militaryDossiers.find((item) => item.id === id);

  if (!timelineItem || !dossier) {
    return (
      <main className="min-h-screen bg-[#0b0a08] px-5 py-12 text-stone-100">
        <div className="mx-auto max-w-4xl rounded-2xl border border-stone-800 bg-[#15120f] p-8">
          <h1 className="text-3xl font-bold text-amber-200">Dossier not found</h1>
          <p className="mt-4 text-stone-300">
            This military archive entry could not be found.
          </p>
          <Link
            to="/military"
            className="mt-6 inline-block rounded-lg bg-amber-700 px-4 py-2 font-semibold text-white hover:bg-amber-600"
          >
            Return to Roman Military Archive
          </Link>
        </div>
      </main>
    );
  }

  const related = legionsTimeline
    .filter(
      (item) =>
        item.id !== timelineItem.id &&
        (item.legionId === timelineItem.legionId ||
          item.periodId === timelineItem.periodId)
    )
    .slice(0, 6);

  return (
    <main className="min-h-screen bg-[#0b0a08] text-stone-100">
      <section className="border-b border-amber-900/40 bg-gradient-to-b from-[#14100c] to-[#0b0a08]">
        <div className="mx-auto max-w-5xl px-5 py-12">
          <Link
            to="/military"
            className="mb-8 inline-block rounded-lg border border-amber-700 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-900/30"
          >
            ← Back to Roman Military Archive
          </Link>

          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-amber-400">
            {LEGION_LABELS[timelineItem.legionId] || `Legio ${timelineItem.legionId}`}
          </p>

          <h1 className="mt-3 text-4xl font-bold leading-tight text-amber-100 md:text-6xl">
            {dossier.title}
          </h1>

          <p className="mt-5 max-w-3xl text-lg leading-8 text-stone-300">
            {dossier.subtitle}
          </p>

          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-stone-800 bg-black/25 p-4">
              <div className="text-xs uppercase tracking-widest text-stone-500">Period</div>
              <div className="mt-1 font-semibold text-amber-300">
                {PERIOD_LABELS[timelineItem.periodId] || timelineItem.periodId}
              </div>
            </div>

            <div className="rounded-xl border border-stone-800 bg-black/25 p-4">
              <div className="text-xs uppercase tracking-widest text-stone-500">Military role</div>
              <div className="mt-1 font-semibold text-amber-300">{timelineItem.role}</div>
            </div>

            <div className="rounded-xl border border-stone-800 bg-black/25 p-4">
              <div className="text-xs uppercase tracking-widest text-stone-500">Coordinates</div>
              <div className="mt-1 font-semibold text-amber-300">
                {timelineItem.lat.toFixed(4)}, {timelineItem.lng.toFixed(4)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl gap-8 px-5 py-10 lg:grid-cols-[1fr_320px]">
        <article className="space-y-6">
          <Field title="Overview">{dossier.overview}</Field>
          <Field title="What happened">{dossier.whatHappened}</Field>
          <Field title="Why it mattered">{dossier.whyItMattered}</Field>
          <Field title="Human perspective">{dossier.humanPerspective}</Field>
          <Field title="Strategic meaning">{dossier.strategicMeaning}</Field>
          <Field title="Supply systems">{dossier.supplySystems}</Field>
          <Field title="Storyworld connection">{dossier.storyworldConnection}</Field>
        </article>

        <aside className="space-y-6">
          <section className="rounded-2xl border border-stone-800 bg-[#15120f] p-5">
            <h2 className="text-xl font-bold text-amber-200">Archive themes</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {dossier.relatedThemes.map((theme) => (
                <span
                  key={theme}
                  className="rounded-full bg-stone-800 px-3 py-1 text-xs text-stone-300"
                >
                  {theme}
                </span>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-stone-800 bg-[#15120f] p-5">
            <h2 className="text-xl font-bold text-amber-200">Related dossiers</h2>
            <div className="mt-4 space-y-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/military/${item.id}`}
                  className="block rounded-xl border border-stone-800 bg-stone-950/60 p-3 hover:border-amber-600"
                >
                  <div className="font-semibold text-amber-300">{item.name}</div>
                  <div className="mt-1 text-xs text-stone-400">
                    {LEGION_LABELS[item.legionId]} · {PERIOD_LABELS[item.periodId] || item.periodId}
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-stone-800 bg-[#15120f] p-5">
            <h2 className="text-xl font-bold text-amber-200">Search terms</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {dossier.relatedSearchTerms.map((term) => (
                <span
                  key={term}
                  className="rounded bg-stone-800 px-2 py-1 text-xs text-stone-300"
                >
                  {term}
                </span>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </main>
  );
}
